import React, { useEffect, useRef, useState } from 'react'
import {
    Box,
    Button,
    IconButton,
    Typography,
    useTheme,
} from '@material-ui/core'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import CloseIcon from '@material-ui/icons/Close'
import { toast } from 'react-toastify'
import { useChannel, useUser } from '../../hooks'
import { MessageDetail } from '../../models'

export interface ThreadAttachment {
    id: number
    file: string | null
}

export interface ThreadMessage {
    id: number
    content: string
    created_date: string
    sender_name: string
    isMine: boolean
    attachments?: ThreadAttachment[]
}

interface Props {
    title?: string
    subtitle?: string
    propertyNotes?: string
    vendorNotes?: string
    messages: ThreadMessage[]
    onSend: (text: string, files: File[]) => Promise<void>
    sending?: boolean
    footerNote?: React.ReactNode
}

const isPdfUrl = (url: string) => /\.pdf(\?|#|$)/i.test(url)

const fileLabel = (name?: string) => {
    if (!name) {
        return 'File'
    }
    const lower = name.toLowerCase()
    if (lower.endsWith('.pdf')) {
        return 'PDF'
    }
    return name
}

const AttachmentThumb = (props: {
    href: string
    previewSrc?: string | null
    label?: string
}) => {
    const { href, previewSrc, label } = props
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            title={label || 'Attachment'}
            style={{
                display: 'block',
                width: 88,
                height: 88,
                borderRadius: 8,
                overflow: 'hidden',
                background: '#E8EAEA',
                border: '1px solid #D7DADA',
                flexShrink: 0,
                textDecoration: 'none',
            }}
        >
            {previewSrc ? (
                <img
                    src={previewSrc}
                    alt={label || 'Attachment'}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
            ) : (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    style={{
                        height: '100%',
                        padding: 8,
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#555',
                        textAlign: 'center',
                    }}
                >
                    {label || 'File'}
                </Box>
            )}
        </a>
    )
}

const PendingFileThumb = (props: { file: File; onRemove: () => void }) => {
    const { file, onRemove } = props
    const isImage = file.type.startsWith('image/')
    const [url, setUrl] = useState<string | null>(null)

    useEffect(() => {
        const next = URL.createObjectURL(file)
        setUrl(next)
        return () => URL.revokeObjectURL(next)
    }, [file])

    return (
        <Box style={{ position: 'relative', width: 72, height: 72 }}>
            {url ? (
                <AttachmentThumb
                    href={url}
                    previewSrc={isImage ? url : null}
                    label={fileLabel(file.name)}
                />
            ) : null}
            <IconButton
                size="small"
                onClick={onRemove}
                aria-label={`Remove ${file.name}`}
                style={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    background: '#fff',
                    border: '1px solid #D7DADA',
                    width: 22,
                    height: 22,
                    padding: 0,
                }}
            >
                <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
        </Box>
    )
}

const formatTime = (value: string) => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return value
    }
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

export const RFPBidThread = (props: Props) => {
    const {
        title,
        subtitle,
        propertyNotes,
        vendorNotes,
        messages,
        onSend,
        sending,
        footerNote,
    } = props
    const theme = useTheme()
    const [draft, setDraft] = useState('')
    const [files, setFiles] = useState<File[]>([])
    const endRef = useRef<HTMLDivElement>(null)
    const fileRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        endRef.current?.scrollIntoView({ block: 'end' })
    }, [messages.length])

    const submit = async () => {
        const text = draft.trim()
        if ((!text && files.length === 0) || sending) {
            return
        }
        await onSend(text, files)
        setDraft('')
        setFiles([])
        if (fileRef.current) {
            fileRef.current.value = ''
        }
    }

    const noteCard = (label: string, body: string) => (
        <Box
            style={{
                background: '#F7F8F8',
                border: '1px solid #E6E8E8',
                borderRadius: 8,
                padding: '10px 12px',
            }}
        >
            <Typography
                style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    color: theme.palette.grey[600],
                    marginBottom: 4,
                }}
            >
                {label}
            </Typography>
            <Typography style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
                {body}
            </Typography>
        </Box>
    )

    return (
        <Box
            display="flex"
            flexDirection="column"
            style={{ height: '100%', minHeight: 0 }}
        >
            {(title || subtitle) && (
                <Box
                    style={{
                        padding: '14px 16px 10px',
                        borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                >
                    {title ? (
                        <Typography style={{ fontWeight: 700, fontSize: 16 }}>
                            {title}
                        </Typography>
                    ) : null}
                    {subtitle ? (
                        <Typography
                            color="textSecondary"
                            style={{ fontSize: 13, marginTop: 2 }}
                        >
                            {subtitle}
                        </Typography>
                    ) : null}
                </Box>
            )}
            <Box
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    padding: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                }}
            >
                {propertyNotes
                    ? noteCard('Property note', propertyNotes)
                    : null}
                {vendorNotes ? noteCard('Vendor note', vendorNotes) : null}
                {messages.length === 0 ? (
                    <Typography
                        color="textSecondary"
                        style={{ fontSize: 13, marginTop: 8 }}
                    >
                        No messages yet. Notes above are from the bid.
                    </Typography>
                ) : (
                    messages.map((message) => (
                        <Box
                            key={message.id}
                            display="flex"
                            justifyContent={
                                message.isMine ? 'flex-end' : 'flex-start'
                            }
                        >
                            <Box
                                style={{
                                    maxWidth: '84%',
                                    background: message.isMine
                                        ? theme.palette.primary.light
                                        : '#F2F3F3',
                                    borderRadius: message.isMine
                                        ? '12px 12px 2px 12px'
                                        : '12px 12px 12px 2px',
                                    padding: '8px 12px',
                                }}
                            >
                                <Typography
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: theme.palette.grey[700],
                                        marginBottom: 2,
                                    }}
                                >
                                    {message.sender_name}
                                </Typography>
                                {message.content ? (
                                    <Typography
                                        style={{
                                            fontSize: 14,
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {message.content}
                                    </Typography>
                                ) : null}
                                {message.attachments &&
                                message.attachments.some((row) => row.file) ? (
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        style={{ gap: 8, marginTop: 8 }}
                                    >
                                        {message.attachments.map((attachment) =>
                                            attachment.file ? (
                                                <AttachmentThumb
                                                    key={attachment.id}
                                                    href={attachment.file}
                                                    previewSrc={
                                                        isPdfUrl(
                                                            attachment.file,
                                                        )
                                                            ? null
                                                            : attachment.file
                                                    }
                                                    label={
                                                        isPdfUrl(
                                                            attachment.file,
                                                        )
                                                            ? 'PDF'
                                                            : 'Attachment'
                                                    }
                                                />
                                            ) : null,
                                        )}
                                    </Box>
                                ) : null}
                                <Typography
                                    color="textSecondary"
                                    style={{ fontSize: 11, marginTop: 4 }}
                                >
                                    {formatTime(message.created_date)}
                                </Typography>
                            </Box>
                        </Box>
                    ))
                )}
                <div ref={endRef} />
            </Box>
            <Box
                style={{
                    borderTop: `1px solid ${theme.palette.grey[200]}`,
                    padding: 12,
                }}
            >
                {files.length > 0 ? (
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        style={{ gap: 6, marginBottom: 8 }}
                    >
                        {files.map((file, index) => (
                            <PendingFileThumb
                                key={`${file.name}-${file.size}-${index}`}
                                file={file}
                                onRemove={() =>
                                    setFiles((prev) =>
                                        prev.filter((_, i) => i !== index),
                                    )
                                }
                            />
                        ))}
                    </Box>
                ) : null}
                <Box
                    display="flex"
                    alignItems="stretch"
                    style={{
                        border: `1px solid ${theme.palette.grey[300]}`,
                        borderRadius: 8,
                        overflow: 'hidden',
                        minHeight: 56,
                    }}
                >
                    <textarea
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        placeholder="Write a message"
                        rows={2}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault()
                                submit()
                            }
                        }}
                        style={{
                            flex: 1,
                            resize: 'none',
                            border: 'none',
                            outline: 'none',
                            padding: '10px 12px',
                            fontFamily: 'inherit',
                            fontSize: 14,
                            lineHeight: 1.4,
                            background: 'transparent',
                        }}
                    />
                    <input
                        ref={fileRef}
                        type="file"
                        hidden
                        multiple
                        accept="image/*,application/pdf"
                        onChange={(event) => {
                            const next = Array.from(event.target.files || [])
                            if (next.length) {
                                setFiles((prev) => [...prev, ...next])
                            }
                            event.target.value = ''
                        }}
                    />
                    <Box
                        display="flex"
                        alignItems="center"
                        style={{
                            gap: 4,
                            padding: '0 8px 0 4px',
                            flexShrink: 0,
                        }}
                    >
                        <IconButton
                            onClick={() => fileRef.current?.click()}
                            aria-label="Attach files"
                        >
                            <AttachFileIcon style={{ fontSize: 26 }} />
                        </IconButton>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={
                                sending || (!draft.trim() && files.length === 0)
                            }
                            onClick={submit}
                            style={{
                                textTransform: 'none',
                                fontWeight: 700,
                                minWidth: 72,
                                height: 36,
                            }}
                        >
                            {sending ? 'Sending…' : 'Send'}
                        </Button>
                    </Box>
                </Box>
                {footerNote ? (
                    <Box style={{ marginTop: 8 }}>{footerNote}</Box>
                ) : null}
            </Box>
        </Box>
    )
}

interface AuthProps {
    channelId: number
    title?: string
    subtitle?: string
    propertyNotes?: string
    vendorNotes?: string
    afterCreateMessage?: (message: MessageDetail) => void
}

export const AuthenticatedRFPBidThread = (props: AuthProps) => {
    const {
        channelId,
        title,
        subtitle,
        propertyNotes,
        vendorNotes,
        afterCreateMessage,
    } = props
    const { workspaceUser } = useUser()
    const { messageList, createMessage } = useChannel(channelId)
    const [sending, setSending] = useState(false)

    const messages: ThreadMessage[] = (messageList || []).map((message) => ({
        id: message.id,
        content: message.content,
        created_date: message.created_date,
        sender_name: message.sender_name,
        isMine: message.sender === workspaceUser?.id,
        attachments: message.attachments,
    }))

    return (
        <RFPBidThread
            title={title}
            subtitle={subtitle}
            propertyNotes={propertyNotes}
            vendorNotes={vendorNotes}
            messages={messages}
            sending={sending}
            onSend={async (text, files) => {
                const body = new FormData()
                body.append('channel', String(channelId))
                if (text) {
                    body.append('content', text)
                }
                if (files.length > 0) {
                    body.append('attachments_len', String(files.length))
                    files.forEach((file, index) => {
                        body.append(`attachment${index}`, file)
                    })
                }
                setSending(true)
                try {
                    const res = await createMessage(body)
                    afterCreateMessage?.(res.data)
                } catch {
                    toast.error('Unable to send that message.')
                } finally {
                    setSending(false)
                }
            }}
        />
    )
}
